import { isNull } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import { ContractualDataAnalytics, DoraAnalyticsChart, NetworkDiagramData, OutstandingContractualInformationPieAnalytics, OutstandingContractualInformationPieCategories, SelectedThirdPartyCoverage, ThirdPartyCoverageAnalytics, ThirdPartyFunctionCompanyCoveragePieAnalytics } from './types';
import { getDoraAnalyticsRoot } from '../../store/selectors';

const categorizeContractualData = (data: ContractualDataAnalytics) => {
    const { doraSupplyChainAssessmentId, doraSupplyChainContractId } = data;
    if (doraSupplyChainAssessmentId && doraSupplyChainContractId) {
        return OutstandingContractualInformationPieCategories.COMPLETE;
    }
    if (isNull(doraSupplyChainAssessmentId) && isNull(doraSupplyChainContractId)) {
        return OutstandingContractualInformationPieCategories.MISSING_BOTH;
    }
    return OutstandingContractualInformationPieCategories.MISSING_ONE;
};

export const getTileInView = (state: ApplicationState): DoraAnalyticsChart | null => getDoraAnalyticsRoot(state).tileInView;

export const getFetchingAnalytics = (state: ApplicationState): DoraAnalyticsChart[] => getDoraAnalyticsRoot(state).fetchingAnalytics;

export const getThirdPartyCoverageAnalytics = (state: ApplicationState): ThirdPartyCoverageAnalytics[] => getDoraAnalyticsRoot(state).thirdPartyCoverage;

export const getPaginatedThirdPartyFunctionCompanyData = (state: ApplicationState): SelectedThirdPartyCoverage[] => getDoraAnalyticsRoot(state).selectedThirdPartyCoverageTable;

export const getThirdPartyFunctionCompanyPieData = (state: ApplicationState): ThirdPartyFunctionCompanyCoveragePieAnalytics[] => getDoraAnalyticsRoot(state).selectedThirdPartyCoveragePie;
export const getThirdPartyName = (state: ApplicationState): string => getDoraAnalyticsRoot(state).selectedThirdPartyCompanyName;
export const getThirdPartyCompanyId = (state: ApplicationState): number | null => getDoraAnalyticsRoot(state).selectedThirdPartyCompanyId;

export const getIsFetchingThirdPartyCoverageTableData = (state: ApplicationState): boolean => getDoraAnalyticsRoot(state).fetchingSelectedThirdPartyCoverageTable;
export const getThirdPartyCoverageFilters = (state: ApplicationState): TableFilters => getDoraAnalyticsRoot(state).selectedThirdPartyCoverageTableFilters;
export const getThirdPartyCoveragePageNumber = (state: ApplicationState): number => getDoraAnalyticsRoot(state).selectedThirdPartyCoverageTablePageNumber;
export const getThirdPartyCoverageColumnSort = (state: ApplicationState): ColumnSort | undefined => getDoraAnalyticsRoot(state).selectedThirdPartyCoverageTableColumnSort;
export const getThirdPartyCoveragePageSize = (state: ApplicationState): number => getDoraAnalyticsRoot(state).selectedThirdPartyCoverageTablePageSize;
export const getTotalFunctionCompanies = (state: ApplicationState): number => getDoraAnalyticsRoot(state).totalFunctionCompanies;

export const getNetworkDiagramAnalytics = (state: ApplicationState): NetworkDiagramData => getDoraAnalyticsRoot(state).networkDiagramData;

export const getContractualDataModalOpen = (state: ApplicationState): boolean => getDoraAnalyticsRoot(state).contractualDataModalOpen;

export const getSelectedContractualDataAnalytics = (state: ApplicationState): ContractualDataAnalytics[] => {
    const selectedPieSegment = getDoraAnalyticsRoot(state).selectedContractualDataAnalytics;
    const selectedContractualDataAnalytics = [...getDoraAnalyticsRoot(state).contractualDataAnalytics].filter(item => {
        if (selectedPieSegment === OutstandingContractualInformationPieCategories.MISSING_BOTH) {
            return !item.doraSupplyChainAssessmentId && !item.doraSupplyChainContractId;
        }
        if (selectedPieSegment === OutstandingContractualInformationPieCategories.COMPLETE) {
            return item.doraSupplyChainAssessmentId && item.doraSupplyChainContractId;
        }
        if (selectedPieSegment === OutstandingContractualInformationPieCategories.MISSING_ONE) {
            return (item.doraSupplyChainAssessmentId && !item.doraSupplyChainContractId) || (!item.doraSupplyChainAssessmentId && item.doraSupplyChainContractId);
        }
    });
    return selectedContractualDataAnalytics;
};

export const getContractualDataAnalyticsPieData = (state: ApplicationState): OutstandingContractualInformationPieAnalytics[] => {
    const analyticsData = getDoraAnalyticsRoot(state).contractualDataAnalytics;
    const allCategories = analyticsData.reduce((acc, curr) => {
        const category = categorizeContractualData(curr);
        if (acc[category]) {
            acc[category] += 1;
        } else {
            acc[category] = 1;
        }
        return acc;
    }, {} as Record<OutstandingContractualInformationPieCategories, number>);

    return Object.entries(allCategories).map(([key, value]) => ({
        key: key as OutstandingContractualInformationPieCategories,
        value
    }));
};
